// Offsite Tickets - mock data + date helpers.
// Ported from the "Valet Concierge Pro" prototype (offsite route) and kept as
// mock data. Front-desk console for hotels reselling pre-bought offsite parking:
// match tickets to pre-booked guests, take payment, validate arrivals, stop
// leakage. Restyled onto the Drivo console design system (see ../constants `T`).

import { DateTime } from "luxon";

/* ---------- date helpers ---------- */

export const toDateInput = (d, timezone) => DateTime
  .fromJSDate(d)
  .setZone(timezone || "local")
  .toISODate();

export const addDays = (d, n, timezone) => DateTime
  .fromJSDate(d)
  .setZone(timezone || "local")
  .plus({ days: n })
  .toJSDate();

export const nightsBetween = (from, to) => {
  const ms = new Date(to).getTime() - new Date(from).getTime();
  return Math.max(1, Math.round(ms / (24 * 3600000)));
};

const WEEK = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const parseDay = (isoDate) => new Date(`${String(isoDate || "").slice(0, 10)}T00:00:00Z`);

export const fmtDay = (isoDate) => {
  const d = parseDay(isoDate);
  return `${WEEK[d.getUTCDay()]} ${d.getUTCDate()} ${MON[d.getUTCMonth()]}`;
};

export const fmtShort = (isoDate) => {
  const d = parseDay(isoDate);
  return `${d.getUTCDate()} ${MON[d.getUTCMonth()]}`;
};

export const nextVoucherId = () => `DRV-${Math.floor(48830 + Math.random() * 999)}`;

/* ---------- reference date + seed data ---------- */

const REF = new Date();
const d0 = toDateInput(REF);
const d1 = toDateInput(addDays(REF, 1));
const d2 = toDateInput(addDays(REF, 2));
const d3 = toDateInput(addDays(REF, 3));
const dMinus1 = toDateInput(addDays(REF, -1));

export const NIGHTLY_RATE = 45; // AUD per night for offsite tickets

export const STAFF = ["Nick", "Ben", "Felicity", "Andrew", "David", "Sarah"];

export const initialLots = [
  { id: "lot-hotel", name: "Hotel Car Park", distance: "On-site", purchased: 1, issued: 0, redeemed: 0 },
  {
    id: "lot-wilsons",
    name: "Adelaide Markets Wilsons",
    distance: "7 min walk",
    purchased: 120,
    issued: 74,
    redeemed: 61,
  },
];

// Pre-booked bookings have paid=true (they paid online). Walk-in / on-behalf
// bookings start unpaid and must have payment taken before ticket allocation.
export const initialBookings = [
  {
    ref: "BK-77214",
    guestName: "Nick",
    email: "nick@drivo.com.au",
    plate: "MRT 88J",
    checkIn: d0,
    checkOut: d2,
    parkingRequested: true,
    source: "pre-booked",
    paid: true,
    amount: 90,
    status: "awaiting",
  },
  {
    ref: "BK-77219",
    guestName: "Ben",
    email: "ben@drivo.com.au",
    checkIn: d0,
    checkOut: d1,
    parkingRequested: true,
    source: "pre-booked",
    paid: true,
    amount: 45,
    status: "awaiting",
  },
  {
    ref: "BK-77225",
    guestName: "Felicity",
    email: "felicity@drivo.com.au",
    plate: "TGV 402",
    checkIn: d1,
    checkOut: d3,
    parkingRequested: true,
    source: "pre-booked",
    paid: true,
    amount: 90,
    status: "awaiting",
  },
  {
    ref: "BK-77231",
    guestName: "Andrew",
    email: "andrew@drivo.com.au",
    checkIn: d0,
    checkOut: d3,
    parkingRequested: true,
    source: "pre-booked",
    paid: true,
    amount: 135,
    status: "awaiting",
  },
  // On-behalf booking that still needs payment at the desk
  {
    ref: "BK-77240",
    guestName: "David",
    email: "david@drivo.com.au",
    plate: "DES 991",
    checkIn: d0,
    checkOut: d2,
    parkingRequested: true,
    source: "walk-in",
    paid: false,
    amount: 90,
    status: "awaiting",
  },
  // Already allocated / redeemed samples
  {
    ref: "BK-77188",
    guestName: "Sarah",
    email: "sarah@drivo.com.au",
    plate: "BXR 44K",
    checkIn: dMinus1,
    checkOut: d1,
    parkingRequested: true,
    source: "pre-booked",
    paid: true,
    amount: 45,
    voucherId: "DRV-48812",
    lotId: "lot-hotel",
    issuedBy: "Ben",
    issuedAtRelative: "yesterday",
    redeemedAtRelative: "yesterday",
    status: "redeemed",
  },
  {
    ref: "BK-77192",
    guestName: "Nick",
    email: "nick@drivo.com.au",
    plate: "CJH 021",
    checkIn: d0,
    checkOut: d1,
    parkingRequested: true,
    source: "pre-booked",
    paid: true,
    amount: 45,
    voucherId: "DRV-48819",
    lotId: "lot-wilsons",
    issuedBy: "Nick",
    issuedAtRelative: "earlier today",
    status: "prepaid",
  },
  {
    ref: "BK-77201",
    guestName: "Andrew",
    email: "andrew@drivo.com.au",
    checkIn: d0,
    checkOut: d1,
    parkingRequested: true,
    source: "pre-booked",
    paid: true,
    amount: 45,
    voucherId: "DRV-48824",
    lotId: "lot-wilsons",
    issuedBy: "Andrew",
    issuedAtRelative: "earlier today",
    flagNote: "Plate to follow - guest arriving from airport",
    status: "prepaid",
  },
  {
    ref: "WK-00042",
    guestName: "Sarah",
    plate: "KZR 774",
    checkIn: d0,
    checkOut: d1,
    parkingRequested: true,
    source: "walk-in",
    paid: true,
    amount: 45,
    paidAtRelative: "earlier today",
    voucherId: "DRV-48790",
    lotId: "lot-wilsons",
    issuedBy: "Felicity",
    issuedAtRelative: "earlier today",
    flagNote: "No matching hotel reservation - leakage check",
    status: "flagged",
  },
];

/* ---------- status -> Tag tone mapping ---------- */

export const STATUS_LABEL = {
  awaiting: "Awaiting",
  prepaid: "Pre-paid",
  allocated: "Allocated",
  redeemed: "Redeemed",
  flagged: "Flagged",
};

export const STATUS_TONE = {
  awaiting: "muted",
  prepaid: "info",
  allocated: "info",
  redeemed: "ok",
  flagged: "warn",
};
